import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked, viewChild } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroCheck, heroPlus, heroXMark } from '@ng-icons/heroicons/outline';
import { NgpMenuTrigger } from 'ng-primitives/menu';
import { IfPermissionDirective } from '../../../../core/auth/permission.directive';
import {
  ToastrService,
  UiBadge,
  UiBulkActionMenuEmpty,
  UiButton,
  UiIconButton,
  UiInput,
  UiMenuItem,
  UiMenuPanel,
  UiMenuSeparator,
} from '../../../../shared/ui';
import type { ConversationLabel } from '../../../../core/api/generated/models/ConversationLabel';
import { ConversationsApiService } from '../../data/conversations-api.service';
import { ConversationsStore } from '../../data/conversations.store';
import { DEFAULT_LABEL_COLOR, LabelColorPickerComponent } from '../label-color-picker/label-color-picker';

/**
 * Inline picker for the labels assigned to a single conversation. Owns its own state
 * (assigned + library) so it can be dropped into the details panel without bloating the
 * store with per-conversation label slices. Optimistic update with rollback on failure.
 *
 * Users with `conversations:base:labels:manage` can also create a brand-new label inline,
 * which is auto-assigned to the current conversation in one flow.
 */
@Component({
  selector: 'app-labels-picker',
  imports: [
    NgIcon,
    NgpMenuTrigger,
    IfPermissionDirective,
    UiBadge,
    UiBulkActionMenuEmpty,
    UiButton,
    UiIconButton,
    UiInput,
    UiMenuItem,
    UiMenuPanel,
    UiMenuSeparator,
    LabelColorPickerComponent,
  ],
  providers: [provideIcons({ heroCheck, heroPlus, heroXMark })],
  templateUrl: './labels-picker.html',
  styleUrl: './labels-picker.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LabelsPickerComponent {
  private readonly api = inject(ConversationsApiService);
  private readonly toast = inject(ToastrService);
  private readonly conversationsStore = inject(ConversationsStore);

  readonly conversationId = input.required<string>();

  protected readonly assigned = signal<ConversationLabel[]>([]);
  protected readonly library = signal<ConversationLabel[]>([]);
  protected readonly busyLabelId = signal<string | null>(null);
  private readonly menuTrigger = viewChild(NgpMenuTrigger);

  protected readonly creating = signal(false);
  protected readonly draftName = signal('');
  protected readonly draftColor = signal(DEFAULT_LABEL_COLOR);
  protected readonly saving = signal(false);

  protected readonly available = computed(() => {
    const assignedIds = new Set(this.assigned().map((label) => label.id));
    return this.library().filter((label) => !label.isArchived && !assignedIds.has(label.id));
  });

  protected readonly canSubmit = computed(() => this.draftName().trim().length > 0 && !this.saving());

  constructor() {
    effect(() => {
      const conversationId = this.conversationId();
      untracked(() => {
        this.menuTrigger()?.hide();
        this.cancelCreate();
        this.reload(conversationId);
      });
    });
  }

  protected onAdd(labelId: string): void {
    this.menuTrigger()?.hide();
    this.assign(labelId);
  }

  protected remove(labelId: string): void {
    const conversationId = this.conversationId();
    if (this.busyLabelId() !== null) return;

    const previous = this.assigned();
    this.assigned.set(previous.filter((label) => label.id !== labelId));
    this.busyLabelId.set(labelId);
    this.api.removeLabel(conversationId, labelId).subscribe({
      next: () => {
        this.busyLabelId.set(null);
        // Mirror the priority-change flow: refresh the inbox so the card reflects the
        // mutation immediately, without waiting for a socket round-trip or F5.
        this.conversationsStore.loadConversations({ preserveSelection: true });
      },
      error: () => {
        this.assigned.set(previous);
        this.busyLabelId.set(null);
        this.toast.error('Labels', 'Não foi possível remover a label.');
      },
    });
  }

  protected startCreate(): void {
    this.creating.set(true);
  }

  protected cancelCreate(): void {
    this.creating.set(false);
    this.draftName.set('');
    this.draftColor.set(DEFAULT_LABEL_COLOR);
  }

  protected setDraftName(value: string): void {
    this.draftName.set(value);
  }

  protected setDraftColor(value: string): void {
    this.draftColor.set(value);
  }

  protected submitCreate(): void {
    if (!this.canSubmit()) return;
    const conversationId = this.conversationId();
    const name = this.draftName().trim();
    const color = this.draftColor();
    this.saving.set(true);
    this.api.createLabel({ name, color }).subscribe({
      next: (created) => {
        // Optimistically append the new label to the library and immediately assign it to the
        // conversation. Failure on assign leaves the label in the library (still usable) but
        // surfaces a toast.
        this.library.update((current) => [...current, created]);
        this.api.assignLabel(conversationId, created.id).subscribe({
          next: () => {
            this.assigned.update((current) => [...current, created]);
            this.saving.set(false);
            this.cancelCreate();
            this.menuTrigger()?.hide();
            this.toast.success('Labels', `Label "${created.name}" criada e atribuída.`);
            this.conversationsStore.loadConversations({ preserveSelection: true });
          },
          error: () => {
            this.saving.set(false);
            this.toast.error('Labels', 'Label criada, mas não foi possível atribuir à conversa.');
          },
        });
      },
      error: (err: unknown) => {
        this.saving.set(false);
        const message = isConflict(err) ? 'Já existe uma label com este nome.' : 'Não foi possível criar a label.';
        this.toast.error('Labels', message);
      },
    });
  }

  private assign(labelId: string): void {
    const conversationId = this.conversationId();
    const label = this.library().find((entry) => entry.id === labelId);
    if (!label || this.busyLabelId() !== null) return;

    const previous = this.assigned();
    this.assigned.set([...previous, label]);
    this.busyLabelId.set(labelId);
    this.api.assignLabel(conversationId, labelId).subscribe({
      next: () => {
        this.busyLabelId.set(null);
        this.conversationsStore.loadConversations({ preserveSelection: true });
      },
      error: () => {
        this.assigned.set(previous);
        this.busyLabelId.set(null);
        this.toast.error('Labels', 'Não foi possível atribuir a label.');
      },
    });
  }

  private reload(conversationId: string): void {
    this.api.listLabels({ includeArchived: false, limit: 100 }).subscribe({
      next: (response) => this.library.set([...response.data]),
      error: () => this.toast.error('Labels', 'Não foi possível carregar a biblioteca de labels.'),
    });
    this.api.listAssignedLabels(conversationId).subscribe({
      next: (response) => this.assigned.set([...response.data]),
      error: () => this.toast.error('Labels', 'Não foi possível carregar as labels da conversa.'),
    });
  }
}

function isConflict(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const status = (error as { status?: number }).status;
  return status === 409;
}
